import React, { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES,
  RichTextEditor,
  ToggleSwitch,
  type RichTextEditorVariable,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './AccordionShowcase.css'

const LEADING_TOGGLE_ITEMS = [
  { id: 'new-booking', title: 'New booking template' },
  { id: 'confirm-booking', title: 'Confirm booking template' },
  {
    id: 'long-title',
    title: 'Reschedule appointment template with a very long title',
  },
]

const SMS_VARIABLES: RichTextEditorVariable[] = [
  { id: 'patient_name', label: 'Patient name', value: '{patient_name}' },
  { id: 'clinic_name', label: 'Clinic name', value: '{clinic_name}' },
  { id: 'date', label: 'Date', value: '{date}' },
  { id: 'time', label: 'Time', value: '{time}' },
  { id: 'provider_name', label: 'Provider name', value: '{provider_name}' },
]

/** Wraps `{variable}` placeholders in the editor's variable token markup. */
const withVariableTokens = (template: string): string =>
  template.replace(
    /\{(\w+)\}/g,
    (match, id: string) =>
      `<span class="ds-rich-text-editor__variable" data-variable-id="${id}">${match}</span>`,
  )

const SMS_TEMPLATES = [
  {
    id: 'new-booking',
    title: 'New booking template',
    body: 'Hi {patient_name}, we received your appointment request at {clinic_name} for {date} at {time}.<br><br>A staff member will be contacting you shortly.',
  },
  {
    id: 'confirm-booking',
    title: 'Confirm booking template',
    body: 'Hi {patient_name}, your appointment at {clinic_name} is confirmed.<br><br>Appointment details:<br>Date: {date}<br>Time: {time}<br>Provider: {provider_name}',
  },
  {
    id: 'reschedule',
    title: 'Reschedule appointment template',
    body: 'Hi {patient_name}, your appointment at {clinic_name} was rescheduled:<br><br>Date: {date}<br>Time: {time}<br>Provider: {provider_name}',
  },
]
const AccordionShowcase: React.FC = () => {
  const [smsEnabled, setSmsEnabled] = useState<Record<string, boolean>>({
    'new-booking': true,
    'confirm-booking': true,
    reschedule: false,
  })
  const [enabledItems, setEnabledItems] = useState<Record<string, boolean>>({
    'new-booking': true,
    'confirm-booking': true,
    'long-title': false,
  })
  const [allowMultiple, setAllowMultiple] = useState(false)
  const [showInfo, setShowInfo] = useState(true)
  const [title, setTitle] = useState('Title')
  const [description, setDescription] = useState('Description text added here.')
  const [groupedExpanded, setGroupedExpanded] = useState(false)
  const [itemCount, setItemCount] = useState(3)

  const groupedAccordionKey = `${itemCount}-${groupedExpanded}`
  const groupedDefaultOpenIds = groupedExpanded
    ? ['accordion-showcase-grouped-0']
    : []

  const handleItemCountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const next = Number(event.target.value)

    if (Number.isFinite(next)) {
      setItemCount(Math.min(5, Math.max(2, Math.round(next))))
    }
  }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Grouped',
        fields: [
          {
            id: 'accordion-allow-multiple',
            label: 'Allow multiple open',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="accordion-allow-multiple"
                  type="checkbox"
                  checked={allowMultiple}
                  onChange={(event) => setAllowMultiple(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'accordion-item-count',
            label: 'Item count',
            control: (
              <input
                id="accordion-item-count"
                className="ui-field-control"
                type="number"
                min={2}
                max={5}
                value={itemCount}
                onChange={handleItemCountChange}
              />
            ),
          },
          {
            id: 'accordion-grouped-expanded',
            label: 'First item expanded',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="accordion-grouped-expanded"
                  type="checkbox"
                  checked={groupedExpanded}
                  onChange={(event) => setGroupedExpanded(event.target.checked)}
                />
                <span>Grouped section</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'accordion-title',
            label: 'Title',
            control: (
              <input
                id="accordion-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'accordion-description',
            label: 'Description',
            control: (
              <textarea
                id="accordion-description"
                className="ui-field-control ui-field-control--textarea"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            ),
          },
          {
            id: 'accordion-show-info',
            label: 'Info icon',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="accordion-show-info"
                  type="checkbox"
                  checked={showInfo}
                  onChange={(event) => setShowInfo(event.target.checked)}
                />
                <span>Visible</span>
              </label>
            ),
          },
        ],
      },
    ],
    [allowMultiple, description, groupedExpanded, itemCount, showInfo, title],
  )

  const groupedItems = Array.from({ length: itemCount }, (_, index) => (
    <AccordionItem
      key={`grouped-${index}`}
      id={`accordion-showcase-grouped-${index}`}
      title={`${title} ${index + 1}`}
      showInfo={showInfo}
    >
      {description}
    </AccordionItem>
  ))

  return (
    <>
      <UiPreviewCanvas
        title="Accordion"
        description="Single and grouped layouts — click any accordion header to expand or collapse."
        stageClassName="ui-preview__stage--wide accordion-showcase__stage-wrap"
      >
        <div className={`accordion-showcase__sheet`}>
          <section className={`ui-showcase-live accordion-showcase__block`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Grouped accordion — click headers to expand or collapse items.
            </p>
            <div className="accordion-showcase__preview">
              <Accordion
                key={groupedAccordionKey}
                grouped
                allowMultiple={allowMultiple}
                defaultOpenIds={groupedDefaultOpenIds}
              >
                {groupedItems}
              </Accordion>
            </div>
          </section>

          <section className="accordion-showcase__section accordion-showcase__block">
            <h3 className="accordion-showcase__section-title">Single</h3>
            <div className="accordion-showcase__single-grid">
              <div className="accordion-showcase__example">
                <span className="accordion-showcase__example-label">
                  Starts collapsed
                </span>
                <div className="accordion-showcase__preview">
                  <AccordionItem title={title} showInfo={showInfo}>
                    {description}
                  </AccordionItem>
                </div>
              </div>
              <div className="accordion-showcase__example">
                <span className="accordion-showcase__example-label">
                  Starts expanded
                </span>
                <div className="accordion-showcase__preview">
                  <AccordionItem
                    title={title}
                    showInfo={showInfo}
                    defaultExpanded
                  >
                    {description}
                  </AccordionItem>
                </div>
              </div>
            </div>
          </section>

          <section className="accordion-showcase__section accordion-showcase__block">
            <h3 className="accordion-showcase__section-title">
              Leading toggle
            </h3>
            <div className="accordion-showcase__preview">
              <Accordion allowMultiple>
                {LEADING_TOGGLE_ITEMS.map((item) => (
                  <AccordionItem
                    key={item.id}
                    id={`leading-toggle-${item.id}`}
                    title={item.title}
                    leading={
                      <ToggleSwitch
                        aria-label={`Enable ${item.title}`}
                        checked={enabledItems[item.id]}
                        onChange={(next) =>
                          setEnabledItems((prev) => ({
                            ...prev,
                            [item.id]: next,
                          }))
                        }
                      />
                    }
                  >
                    {description}
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          <section className="accordion-showcase__section accordion-showcase__block">
            <h3 className="accordion-showcase__section-title">SMS templates</h3>
            <div className="accordion-showcase__preview accordion-showcase__stack">
              {SMS_TEMPLATES.map((template) => (
                <AccordionItem
                  key={template.id}
                  id={`sms-template-${template.id}`}
                  title={template.title}
                  defaultExpanded
                  leading={
                    <ToggleSwitch
                      aria-label={`Enable ${template.title}`}
                      checked={smsEnabled[template.id]}
                      onChange={(next) =>
                        setSmsEnabled((prev) => ({
                          ...prev,
                          [template.id]: next,
                        }))
                      }
                    />
                  }
                >
                  <RichTextEditor
                    variant="sms-notes"
                    defaultValue={withVariableTokens(template.body)}
                    toolbarGroups={DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES}
                    variables={SMS_VARIABLES}
                    disabled={!smsEnabled[template.id]}
                  />
                </AccordionItem>
              ))}
            </div>
          </section>
          <section className="accordion-showcase__section accordion-showcase__block">
            <h3 className="accordion-showcase__section-title">Grouped</h3>
            <div className="accordion-showcase__preview">
              <Accordion
                key={`grouped-section-${groupedAccordionKey}`}
                grouped
                allowMultiple={allowMultiple}
                defaultOpenIds={groupedDefaultOpenIds}
              >
                {groupedItems}
              </Accordion>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Accordion"
        sections={propertySections}
      />
    </>
  )
}

export default AccordionShowcase
